/* ==========================================================================
   Dosha Quiz — BioVeda
   Flow (follows radhidevlukia.com/quiz reference):
     intro + email up front → Part 1 (Prakruti) category pages →
     Part 2 (Vikruti) pages → results.
   Questions are grouped into pages by category, parsed from the first line of
   question_text. Options carry A/B/C badges (A=Vata, B=Pitta, C=Kapha).
   Scoring = PLAN §6 (verified). Vanilla JS, evergreen browsers.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTOR = '[data-section-type="dosha-quiz"]';
  var SECTIONS = ['prakruti', 'vikruti'];

  /* ---------- helpers ---------------------------------------------------- */
  function h(tag, props, children) {
    var node = document.createElement(tag);
    if (props) {
      Object.keys(props).forEach(function (key) {
        var val = props[key];
        if (val == null) return;
        if (key === 'class') node.className = val;
        else if (key === 'text') node.textContent = val;
        else if (key === 'html') node.innerHTML = val;
        else if (key.indexOf('on') === 0 && typeof val === 'function') {
          node.addEventListener(key.slice(2).toLowerCase(), val);
        } else {
          node.setAttribute(key, val);
        }
      });
    }
    (children || []).forEach(function (child) {
      if (child == null) return;
      node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
    });
    return node;
  }

  function norm(value) { return String(value == null ? '' : value).toLowerCase().trim(); }

  function chunk(arr, size) {
    var out = [];
    for (var i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  // Split a category header (first line) off a question, and strip a stray
  // leading "12. " number. Category = short title line, no trailing : or ?.
  function parseQuestion(text) {
    text = String(text == null ? '' : text);
    var category = null;
    var question = text.trim();
    var idx = text.indexOf('\n\n');
    if (idx > -1) {
      var head = text.slice(0, idx).trim();
      var rest = text.slice(idx + 2).trim();
      if (head && head.length <= 40 && !/[:?]$/.test(head)) {
        category = head;
        question = rest;
      }
    }
    question = question.replace(/^\s*\d+\.\s*/, '');
    return { category: category, question: question };
  }

  function prettyDosha(key) {
    if (!key) return '';
    return String(key).split('_').map(function (part) {
      return part ? part.charAt(0).toUpperCase() + part.slice(1) : part;
    }).join('-');
  }

  /* ---------- minimal, safe rich-text renderer -------------------------- */
  // Result bodies are merchant-authored plain text that sometimes contains a
  // little markdown (**bold**, leading #). Escape everything first, then apply
  // a tiny safe subset, so it renders cleanly whether or not markdown is used.
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function mdInline(s) {
    return escapeHtml(s).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  }
  function renderRichText(text) {
    var blocks = String(text == null ? '' : text).replace(/\r\n/g, '\n').split(/\n{2,}/);
    var html = '';
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i].trim();
      if (!block) continue;
      var hm = block.match(/^#{1,3}\s+([\s\S]*)$/);
      if (hm) {
        html += '<h4 class="dq__rt-heading">' + mdInline(hm[1].replace(/\n+/g, ' ')) + '</h4>';
      } else {
        var lines = block.split('\n');
        for (var j = 0; j < lines.length; j++) lines[j] = mdInline(lines[j]);
        html += '<p class="dq__rt-p">' + lines.join('<br>') + '</p>';
      }
    }
    return html;
  }

  /* ---------- scoring engine (PLAN §6) ---------------------------------- */
  var VIKRUTI_PRECEDENCE = ['vata', 'pitta', 'kapha'];   // tie-break order
  var DOSHA_ORDER = { vata: 0, pitta: 1, kapha: 2 };      // canonical dual-key order V→P→K

  function dualKey(a, b) {
    return [a, b].sort(function (x, y) { return DOSHA_ORDER[x] - DOSHA_ORDER[y]; }).join('_');
  }

  function classifyVikruti(s) {
    return VIKRUTI_PRECEDENCE.slice().sort(function (a, b) { return s[b] - s[a]; })[0];
  }

  function classifyPrakruti(s, opts) {
    opts = opts || {};
    var tridoshicSpread = opts.tridoshicSpread != null ? opts.tridoshicSpread : 2;
    var dualTopGap = opts.dualTopGap != null ? opts.dualTopGap : 2;
    var dualThirdGap = opts.dualThirdGap != null ? opts.dualThirdGap : 3;
    var ranked = ['vata', 'pitta', 'kapha'].sort(function (a, b) { return s[b] - s[a]; });
    var n1 = ranked[0], n2 = ranked[1], n3 = ranked[2];
    var d1 = s[n1], d2 = s[n2], d3 = s[n3];
    if (d1 - d3 <= tridoshicSpread) return 'tridoshic';
    if (d1 - d2 <= dualTopGap && d2 - d3 >= dualThirdGap) return dualKey(n1, n2);
    return n1;
  }

  function selfTest() {
    var th = { tridoshicSpread: 2, dualTopGap: 2, dualThirdGap: 3 };
    function P(v, p, k) { return { vata: v, pitta: p, kapha: k }; }
    var prakruti = [
      { s: P(9, 8, 8), expect: 'tridoshic' }, { s: P(10, 9, 6), expect: 'vata_pitta' },
      { s: P(9, 5, 8), expect: 'vata_kapha' }, { s: P(6, 10, 9), expect: 'pitta_kapha' },
      { s: P(12, 8, 5), expect: 'vata' }, { s: P(5, 13, 7), expect: 'pitta' },
      { s: P(5, 8, 12), expect: 'kapha' }
    ];
    var vikruti = [
      { s: P(3, 1, 1), expect: 'vata' }, { s: P(1, 4, 2), expect: 'pitta' },
      { s: P(1, 2, 5), expect: 'kapha' }, { s: P(2, 2, 1), expect: 'vata' }
    ];
    var rows = [], fail = 0;
    prakruti.forEach(function (c) {
      var got = classifyPrakruti(c.s, th), ok = got === c.expect; if (!ok) fail++;
      rows.push({ kind: 'prakruti', V: c.s.vata, P: c.s.pitta, K: c.s.kapha, expect: c.expect, got: got, ok: ok });
    });
    vikruti.forEach(function (c) {
      var got = classifyVikruti(c.s), ok = got === c.expect; if (!ok) fail++;
      rows.push({ kind: 'vikruti', V: c.s.vata, P: c.s.pitta, K: c.s.kapha, expect: c.expect, got: got, ok: ok });
    });
    if (window.console && console.table) {
      console.group('[dosha-quiz] scoring self-test — ' + (rows.length - fail) + '/' + rows.length + ' passed');
      console.table(rows);
      console.groupEnd();
    }
    return fail === 0;
  }

  /* ---------- component -------------------------------------------------- */
  function DoshaQuiz(root) {
    this.root = root;
    this.mount = root.querySelector('.dosha-quiz__contain') || root;
    this.data = this.parseData();
    this.storeKey = 'dq:' + (root.getAttribute('data-section-id') || 'default');
    this.debug = new URLSearchParams(window.location.search).has('quizdebug');
    this.answers = {};         // questionId -> { section, dosha, letter }
    this.lead = {};            // { name, email }
    this.index = 0;
    if (!this.data || !this.data.questions || !this.data.questions.length) {
      if (this.debug) console.warn('[dosha-quiz] no quiz data found');
      return;
    }
    this.config = this.data.config || {};
    if (this.debug) selfTest();
    this.buildSteps();
    this.restore();
    this.renderShell();
    this.render();
  }

  DoshaQuiz.prototype.parseData = function () {
    var node = this.root.querySelector('[data-dq-data]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); }
    catch (err) { if (this.debug) console.error('[dosha-quiz] bad JSON', err); return null; }
  };

  DoshaQuiz.prototype.fallbackHeading = function () { return 'Discover Your Dosha'; };
  DoshaQuiz.prototype.fallbackIntro = function () {
    return 'Answer a few questions about your nature and how you have been feeling lately to discover your Ayurvedic mind-body type — and the rituals to bring you back into balance.';
  };

  /* ---------- build the ordered list of pages --------------------------- */
  DoshaQuiz.prototype.buildSteps = function () {
    var data = this.data;
    var maxPerPage = Math.max(1, Number(this.config.questionsPerPage) || 6);
    var bySection = { prakruti: [], vikruti: [] };
    (data.questions || []).forEach(function (q) {
      var sec = norm(q.section);
      if (bySection[sec]) bySection[sec].push(q);
    });
    SECTIONS.forEach(function (sec) {
      bySection[sec].sort(function (a, b) { return (Number(a.order) || 0) - (Number(b.order) || 0); });
    });
    this.bySection = bySection;
    this.totalQuestions = bySection.prakruti.length + bySection.vikruti.length;

    var steps = [{ type: 'intro' }];
    SECTIONS.forEach(function (sec) {
      var si = (data.sectionIntros || []).filter(function (x) { return norm(x.section) === sec; })[0];
      var partTitle = (si && si.title) ? si.title : (sec === 'vikruti' ? 'Part 2: Your Vikruti' : 'Part 1: Your Prakruti');
      if (si && (si.title || si.text)) steps.push({ type: 'sectionIntro', section: sec, intro: si });

      var groups = [];
      var num = 0;
      bySection[sec].forEach(function (q) {
        var parsed = parseQuestion(q.text);
        q._question = parsed.question;
        num += 1; q._num = num;
        if (parsed.category || groups.length === 0) groups.push({ category: parsed.category, items: [q] });
        else groups[groups.length - 1].items.push(q);
      });
      groups.forEach(function (group) {
        chunk(group.items, maxPerPage).forEach(function (items, ci) {
          steps.push({ type: 'questionPage', section: sec, part: partTitle, category: group.category, items: items, continued: ci > 0 });
        });
      });
    });
    steps.push({ type: 'results' });
    this.steps = steps;
  };

  /* ---------- persistence ----------------------------------------------- */
  DoshaQuiz.prototype.persist = function () {
    try {
      sessionStorage.setItem(this.storeKey, JSON.stringify({ answers: this.answers, lead: this.lead, index: this.index }));
    } catch (err) { /* storage unavailable */ }
  };

  DoshaQuiz.prototype.restore = function () {
    try {
      var saved = JSON.parse(sessionStorage.getItem(this.storeKey));
      if (saved && typeof saved === 'object') {
        this.answers = saved.answers || {};
        this.lead = saved.lead || {};
        if (typeof saved.index === 'number' && saved.index >= 0 && saved.index < this.steps.length) this.index = saved.index;
      }
    } catch (err) { /* ignore */ }
  };

  DoshaQuiz.prototype.reset = function () {
    this.answers = {}; this.lead = {}; this.index = 0; this._klaviyoDone = false;
    try { sessionStorage.removeItem(this.storeKey); } catch (err) {}
    this.render();
  };

  /* ---------- navigation ------------------------------------------------- */
  DoshaQuiz.prototype.go = function (delta) {
    var next = this.index + delta;
    if (next < 0 || next >= this.steps.length) return;
    this.index = next;
    this.persist();
    this.render();
    this.focusScreen();
    try { this.root.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (e) {}
  };

  DoshaQuiz.prototype.canAdvance = function (step) {
    if (step.type !== 'questionPage') return true;
    var self = this;
    return step.items.every(function (q) { return !!self.answers[q.id]; });
  };

  /* ---------- rendering -------------------------------------------------- */
  DoshaQuiz.prototype.renderShell = function () {
    var placeholder = this.mount.querySelector('[data-dq-placeholder]');
    if (placeholder) placeholder.setAttribute('hidden', '');
    this.app = h('div', { 'class': 'dq', role: 'group', 'aria-label': 'Dosha quiz' });
    this.mount.appendChild(this.app);
  };

  DoshaQuiz.prototype.render = function () {
    var step = this.steps[this.index];
    this.app.innerHTML = '';
    if (step.type !== 'results') this.app.appendChild(this.renderProgress());
    var screen;
    switch (step.type) {
      case 'intro':        screen = this.renderIntro(); break;
      case 'sectionIntro': screen = this.renderSectionIntro(step); break;
      case 'questionPage': screen = this.renderQuestionPage(step); break;
      case 'results':      screen = this.renderResults(); break;
      default:             screen = h('div');
    }
    screen.classList.add('dq__screen');
    this.app.appendChild(screen);
  };

  DoshaQuiz.prototype.focusScreen = function () {
    var target = this.app.querySelector('[data-dq-focus]');
    if (target) { try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); } }
  };

  DoshaQuiz.prototype.renderProgress = function () {
    var total = this.steps.length;
    var current = this.index;
    var track = h('div', {
      'class': 'dq__seg-track', role: 'progressbar',
      'aria-valuemin': '1', 'aria-valuemax': String(total), 'aria-valuenow': String(current + 1),
      'aria-label': 'Page ' + (current + 1) + ' of ' + total
    });
    for (var i = 0; i < total; i++) {
      track.appendChild(h('span', { 'class': 'dq__seg' + (i <= current ? ' is-filled' : '') }));
    }
    return h('div', { 'class': 'dq__progress' }, [
      track,
      h('p', { 'class': 'dq__progress-label', text: 'Page ' + (current + 1) + ' of ' + total })
    ]);
  };

  // Screen 1 (reference layout): heading + short intro + divider + name/email
  // side-by-side + divider + full-width "Take the quiz →".
  DoshaQuiz.prototype.renderIntro = function () {
    var self = this;
    var intro = this.data.intro || {};
    var lead = this.data.leadCapture || {};
    var requireEmail = this.config.requireEmailBeforeResults !== false;

    var nameInput = h('input', { 'class': 'dq__input', type: 'text', id: 'dq-name', name: 'name', autocomplete: 'given-name', value: this.lead.name || '' });
    var emailInput = h('input', { 'class': 'dq__input', type: 'email', id: 'dq-email', name: 'email', autocomplete: 'email', value: this.lead.email || '' });
    var error = h('p', { 'class': 'dq__field-error', role: 'alert', hidden: 'hidden' });
    var star = requireEmail ? ' *' : '';
    var submitLabel = lead.submitLabel || intro.buttonLabel || 'Take the quiz';

    var optInEnabled = this.config.newsletterOptinEnabled !== false;
    var optInInput = optInEnabled ? h('input', { 'class': 'dq__optin-input', type: 'checkbox', id: 'dq-optin' }) : null;
    if (optInInput) {
      var preChecked = (typeof this.lead.optIn === 'boolean') ? this.lead.optIn : (this.config.newsletterOptinDefault !== false);
      if (preChecked) optInInput.checked = true;
    }

    var form = h('form', { 'class': 'dq__intro', novalidate: 'novalidate' }, [
      h('h1', { 'class': 'dq__title', tabindex: '-1', 'data-dq-focus': '', text: intro.title || this.fallbackHeading() }),
      intro.text
        ? h('div', { 'class': 'dq__lede dq__multiline', text: intro.text })
        : h('p', { 'class': 'dq__lede', text: this.fallbackIntro() }),
      h('hr', { 'class': 'dq__rule' }),
      h('div', { 'class': 'dq__form-grid' }, [
        h('div', { 'class': 'dq__field' }, [
          h('label', { 'class': 'dq__label', 'for': 'dq-name', text: (lead.nameLabel || 'First name') + star }), nameInput
        ]),
        h('div', { 'class': 'dq__field' }, [
          h('label', { 'class': 'dq__label', 'for': 'dq-email', text: (lead.emailLabel || 'Email address') + star }), emailInput
        ])
      ]),
      optInInput ? h('label', { 'class': 'dq__optin', 'for': 'dq-optin' }, [
        optInInput,
        h('span', { 'class': 'dq__optin-text', text: this.config.newsletterOptinLabel || 'Email me my results, offers and Ayurvedic tips' })
      ]) : null,
      error,
      lead.privacyNote ? h('p', { 'class': 'dq__privacy dq__multiline', text: lead.privacyNote }) : null,
      h('hr', { 'class': 'dq__rule' }),
      h('div', { 'class': 'dq__actions dq__actions--block' }, [
        h('button', { 'class': 'dq__btn dq__btn--primary dq__btn--block', type: 'submit' }, [
          submitLabel,
          h('span', { 'class': 'dq__btn-arrow', 'aria-hidden': 'true' }, ['→'])
        ])
      ])
    ]);

    function fail(msg, field) { error.textContent = msg; error.removeAttribute('hidden'); field.focus(); }
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var name = (nameInput.value || '').trim();
      var email = (emailInput.value || '').trim();
      if (requireEmail) {
        if (!name) return fail('Please enter your first name.', nameInput);
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return fail('Please enter a valid email address.', emailInput);
      }
      self.lead = { name: name, email: email, optIn: optInInput ? optInInput.checked : false };
      self.persist();
      self.klaviyoIdentifyLead();   // capture the lead in Klaviyo up front
      if (self.lead.optIn) self.klaviyoSubscribe();   // opt-in → subscribe to the list
      self.go(1);
    });
    return form;
  };

  DoshaQuiz.prototype.renderSectionIntro = function (step) {
    var intro = step.intro || {};
    return h('div', { 'class': 'dq__section-intro' }, [
      intro.title ? h('h2', { 'class': 'dq__title', tabindex: '-1', 'data-dq-focus': '', text: intro.title }) : null,
      intro.text ? h('div', { 'class': 'dq__lede dq__multiline', text: intro.text }) : null,
      this.navRow(step, intro.buttonLabel || 'Continue')
    ]);
  };

  DoshaQuiz.prototype.renderQuestionPage = function (step) {
    var self = this;
    var hasCategory = !!step.category;
    var headingText = (hasCategory ? step.category : step.part) + (step.continued ? ' (continued)' : '');
    var head = h('div', { 'class': 'dq__qpage-head' }, [
      hasCategory ? h('p', { 'class': 'dq__qpage-part', text: step.part }) : null,
      h('h2', { 'class': 'dq__qpage-category', tabindex: '-1', 'data-dq-focus': '', text: headingText })
    ]);
    var wrap = h('div', { 'class': 'dq__qpage' }, [head]);
    step.items.forEach(function (q) { wrap.appendChild(self.renderQuestionBlock(step, q)); });
    wrap.appendChild(this.navRow(step, this.nextLabel()));
    return wrap;
  };

  DoshaQuiz.prototype.renderQuestionBlock = function (step, q) {
    var self = this;
    var saved = this.answers[q.id];
    var fieldset = h('fieldset', { 'class': 'dq__options' }, [
      h('legend', { 'class': 'dq__question' }, [
        h('span', { 'class': 'dq__question-num', 'aria-hidden': 'true', text: q._num + '.' }),
        h('span', { 'class': 'dq__question-text dq__multiline', text: q._question })
      ])
    ]);
    (q.options || []).forEach(function (opt) {
      var id = q.id + '-' + opt.letter;
      var input = h('input', { 'class': 'dq__option-input', type: 'radio', name: q.id, id: id, value: opt.letter });
      if (saved && saved.letter === opt.letter) input.checked = true;
      input.addEventListener('change', function () {
        self.answers[q.id] = { section: step.section, dosha: opt.dosha, letter: opt.letter };
        self.persist();
        self.refreshPageNext(step);
      });
      fieldset.appendChild(h('label', { 'class': 'dq__option', 'for': id }, [
        input,
        h('span', { 'class': 'dq__option-badge', 'aria-hidden': 'true', text: opt.letter }),
        h('span', { 'class': 'dq__option-text dq__multiline', text: opt.text })
      ]));
    });
    return h('div', { 'class': 'dq__qblock' }, [fieldset]);
  };

  DoshaQuiz.prototype.nextLabel = function () {
    var nx = this.steps[this.index + 1];
    if (nx && nx.type === 'results') return 'See my results';
    return 'Next';
  };

  DoshaQuiz.prototype.navRow = function (step, nextText) {
    var self = this;
    var canBack = this.index > 0 && this.steps[this.index - 1].type !== 'results';
    var nextBtn = h('button', {
      'class': 'dq__btn dq__btn--primary', type: 'button',
      onClick: function () { if (self.canAdvance(step)) self.go(1); }, text: nextText
    });
    this._nextBtn = nextBtn;
    if (!this.canAdvance(step)) nextBtn.setAttribute('disabled', '');
    return h('div', { 'class': 'dq__actions dq__actions--split' }, [
      canBack ? h('button', { 'class': 'dq__btn dq__btn--ghost', type: 'button', onClick: function () { self.go(-1); }, text: 'Back' }) : h('span'),
      nextBtn
    ]);
  };

  DoshaQuiz.prototype.refreshPageNext = function (step) {
    if (!this._nextBtn) return;
    if (this.canAdvance(step)) this._nextBtn.removeAttribute('disabled');
    else this._nextBtn.setAttribute('disabled', '');
  };

  /* ---------- scoring use + results ------------------------------------- */
  DoshaQuiz.prototype.tally = function () {
    var t = { prakruti: { vata: 0, pitta: 0, kapha: 0 }, vikruti: { vata: 0, pitta: 0, kapha: 0 } };
    var self = this;
    Object.keys(this.answers).forEach(function (id) {
      var a = self.answers[id];
      if (a && t[a.section] && typeof t[a.section][a.dosha] === 'number') t[a.section][a.dosha] += 1;
    });
    return t;
  };

  DoshaQuiz.prototype.computeResults = function () {
    var scores = this.tally();
    var thresholds = {
      tridoshicSpread: Number(this.config.tridoshicSpread),
      dualTopGap: Number(this.config.dualTopGap),
      dualThirdGap: Number(this.config.dualThirdGap)
    };
    if (isNaN(thresholds.tridoshicSpread)) thresholds.tridoshicSpread = 2;
    if (isNaN(thresholds.dualTopGap)) thresholds.dualTopGap = 2;
    if (isNaN(thresholds.dualThirdGap)) thresholds.dualThirdGap = 3;
    var prakrutiKey = classifyPrakruti(scores.prakruti, thresholds);
    var vikrutiKey = classifyVikruti(scores.vikruti);
    return {
      scores: scores, thresholds: thresholds,
      prakrutiKey: prakrutiKey, vikrutiKey: vikrutiKey,
      prakruti: this.resultEntry('prakruti', prakrutiKey),
      vikruti: this.resultEntry('vikruti', vikrutiKey)
    };
  };

  DoshaQuiz.prototype.resultEntry = function (section, key) {
    return (this.data.results || []).filter(function (r) {
      return norm(r.section) === section && norm(r.resultKey) === key;
    })[0] || null;
  };

  DoshaQuiz.prototype.renderResults = function () {
    var self = this;
    var outcome = this.computeResults();
    if (this.debug) console.info('[dosha-quiz] results', outcome);
    this.completeKlaviyo(outcome);
    var name = (this.lead.name || '').trim();

    function block(modifier, eyebrow, entry, fallbackKey) {
      var body;
      if (entry && entry.body) {
        body = h('div', { 'class': 'dq__result-body dq__rt' });
        body.innerHTML = renderRichText(entry.body);   // escaped + tiny safe subset
      } else {
        body = h('p', { 'class': 'dq__result-body', text: 'Result content is being prepared.' });
      }
      return h('article', { 'class': 'dq__result dq__result--' + modifier }, [
        h('p', { 'class': 'dq__result-eyebrow', text: eyebrow }),
        h('h3', { 'class': 'dq__result-heading', text: (entry && entry.heading) || prettyDosha(fallbackKey) }),
        body
      ]);
    }

    var cta = (this.config.ctaLabel && this.config.ctaUrl)
      ? h('a', { 'class': 'dq__btn dq__btn--primary', href: this.config.ctaUrl, text: this.config.ctaLabel })
      : null;

    var products = h('div', { 'class': 'dq__results-products', 'data-dq-products': '' });
    this.loadProducts(outcome, products);   // async; fills in or stays empty (hidden)

    return h('div', { 'class': 'dq__results' }, [
      name ? h('p', { 'class': 'dq__results-greeting', text: 'Thank you, ' + name + '.' }) : null,
      h('h2', { 'class': 'dq__title', tabindex: '-1', 'data-dq-focus': '', text: 'Your Dosha Profile' }),
      block('prakruti', 'Your Prakruti · natural constitution', outcome.prakruti, outcome.prakrutiKey),
      block('vikruti', 'Your Vikruti · current state', outcome.vikruti, outcome.vikrutiKey),
      products,
      h('div', { 'class': 'dq__actions dq__actions--center' }, [
        cta,
        h('button', { 'class': 'dq__btn dq__btn--ghost', type: 'button', onClick: function () { self.reset(); }, text: 'Start over' })
      ])
    ]);
  };

  // Fetch the recommended collection's product cards (alternate collection
  // view) and inject them, capped to productsPerResult. No collection linked
  // yet → returns quietly, leaving the empty container hidden.
  DoshaQuiz.prototype.loadProducts = function (outcome, container) {
    var self = this;
    var src = this.config.recommendationSource === 'prakruti' ? outcome.prakruti : outcome.vikruti;
    var handle = src && src.collectionHandle;
    if (!handle || !window.fetch) return;
    var limit = Math.max(1, Number(this.config.productsPerResult) || 4);

    fetch('/collections/' + encodeURIComponent(handle) + '?view=dq-products', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) { return res.ok ? res.text() : Promise.reject(res.status); })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var cards = tmp.querySelectorAll('[data-dq-card]');
        if (!cards.length) return;
        var grid = h('div', { 'class': 'dq__products-grid' });
        for (var i = 0; i < cards.length && i < limit; i++) grid.appendChild(cards[i]);
        container.appendChild(h('h3', { 'class': 'dq__products-title', text: self.config.productsHeading || 'Recommended for you' }));
        container.appendChild(grid);
      })
      .catch(function (err) { if (self.debug) console.warn('[dosha-quiz] products fetch failed', err); });
  };

  /* ---------- Klaviyo (PLAN §7) ----------------------------------------- */
  // Uses the on-site `klaviyo` object loaded by the Klaviyo Shopify app embed.
  DoshaQuiz.prototype.klaviyoEnabled = function () {
    if (this.config.enableKlaviyo === false) return false;
    if (window.Shopify && window.Shopify.designMode) return false;   // skip inside the theme editor
    return true;
  };

  DoshaQuiz.prototype.klaviyo = function (method, a, b) {
    if (!this.klaviyoEnabled()) return;
    var kl = window.klaviyo;
    if (!kl || typeof kl[method] !== 'function') {
      if (this.debug) console.warn('[dosha-quiz] klaviyo unavailable (' + method + ')');
      return;
    }
    try { if (method === 'track') kl.track(a, b); else kl[method](a); }
    catch (e) { if (this.debug) console.warn('[dosha-quiz] klaviyo ' + method + ' failed', e); }
  };

  DoshaQuiz.prototype.leadProfile = function () {
    var profile = { email: (this.lead.email || '').trim() };
    var first = (this.lead.name || '').trim();
    if (first) profile.first_name = first;
    return profile;
  };

  // Intro submit — capture the lead up front (survives mid-quiz abandons).
  DoshaQuiz.prototype.klaviyoIdentifyLead = function () {
    if (!(this.lead.email || '').trim()) return;
    var p = this.leadProfile();
    p['Newsletter Opt-In'] = !!this.lead.optIn;
    this.klaviyo('identify', p);
  };

  // Results — add the dosha properties to the profile + fire the event. Once.
  DoshaQuiz.prototype.completeKlaviyo = function (outcome) {
    if (this._klaviyoDone || !(this.lead.email || '').trim()) return;
    this._klaviyoDone = true;
    var prakProp = this.config.klaviyoPrakrutiProperty || 'Prakruti';
    var vikProp = this.config.klaviyoVikrutiProperty || 'Vikruti';
    var prakruti = prettyDosha(outcome.prakrutiKey);
    var vikruti = prettyDosha(outcome.vikrutiKey);

    var id = this.leadProfile();
    id[prakProp] = prakruti;
    id[vikProp] = vikruti;
    id['Newsletter Opt-In'] = !!this.lead.optIn;
    id['Dosha Quiz Completed'] = true;
    id['Dosha Quiz Date'] = new Date().toISOString();
    this.klaviyo('identify', id);

    var props = {};
    props[prakProp] = prakruti;
    props[vikProp] = vikruti;
    props['Prakruti Key'] = outcome.prakrutiKey;
    props['Vikruti Key'] = outcome.vikrutiKey;
    props['Recommended Collection'] = (outcome.vikruti && outcome.vikruti.collectionHandle) || null;
    this.klaviyo('track', this.config.klaviyoEventName || 'Completed Dosha Quiz', props);
    if (this.debug) console.info('[dosha-quiz] klaviyo sent', id, props);
  };

  // Klaviyo public key (Site ID) — from setting, else auto-detected from the embed.
  DoshaQuiz.prototype.klaviyoCompanyId = function () {
    if (this.config.klaviyoCompanyId) return String(this.config.klaviyoCompanyId).trim();
    try {
      var scripts = document.querySelectorAll('script[src]');
      for (var i = 0; i < scripts.length; i++) {
        var m = scripts[i].src.match(/company_id=([A-Za-z0-9]+)/);
        if (m) return m[1];
      }
    } catch (e) {}
    return null;
  };

  // Subscribe the opted-in visitor to a Klaviyo list (client Subscribe API).
  // Skips quietly until a list id is configured. The client endpoint always
  // runs the list's double opt-in for compliance.
  DoshaQuiz.prototype.klaviyoSubscribe = function () {
    if (!this.klaviyoEnabled() || !window.fetch) return;
    var self = this;
    var email = (this.lead.email || '').trim();
    var listId = (this.config.klaviyoListId || '').trim();
    var companyId = this.klaviyoCompanyId();
    if (!email || !listId || !companyId) {
      if (this.debug) console.warn('[dosha-quiz] klaviyo subscribe skipped (email/list id/company id missing)');
      return;
    }
    var profileAttrs = { email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } };
    var first = (this.lead.name || '').trim();
    if (first) profileAttrs.first_name = first;
    var body = {
      data: {
        type: 'subscription',
        attributes: { profile: { data: { type: 'profile', attributes: profileAttrs } } },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
    fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2026-04-15' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (self.debug) console.info('[dosha-quiz] klaviyo subscribe status', res.status);
    }).catch(function (err) { if (self.debug) console.warn('[dosha-quiz] klaviyo subscribe failed', err); });
  };

  /* ---------- boot ------------------------------------------------------- */
  function boot(root) {
    if (!root || root.dataset.dqBooted === '1') return;
    root.dataset.dqBooted = '1';
    try { new DoshaQuiz(root); }
    catch (err) { if (window.console) console.error('[dosha-quiz] init failed', err); }
  }

  function init(scope) {
    var nodes = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) boot(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
